import { parseDbTimestamp } from "@repo/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Download,
  FileSignature,
  Loader2,
  Pen,
  QrCode,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState, useTransition } from "react";
import { QRCodeModal } from "@/components/qr-code-modal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useClientIdentity, VerifiedIdBadge, VerifiedIdDialog } from "@/components/verified-id";
import { useConfirm } from "@/hooks/use-confirm";
import { apiErrorCode, apiErrorMessage, del, get, post } from "@/lib/api/_client";
import { useSaveClientContact } from "@/lib/hooks/use-client-detail";
import { useIduraMode } from "@/lib/hooks/use-idura";
import { useThirdPartyFeatures } from "@/lib/hooks/use-third-party-features";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./signature-section.i18n";

type SignatureOrder = any;

type SignatureSectionProps = {
  flowInstanceId: string;
  pdfCount: number;
  refreshKey?: number;
  clientId: string;
  clientEmail: string | null;
  clientMobilePhone: string | null;
  /** Called after any order change (create/cancel) — the process step may have moved. */
  onSignatureChange?: () => void;
  /** Which signing methods the flow allows. Default: both. */
  enableEidSigning?: boolean;
  enableDigitalSigning?: boolean;
};

/** How often the orders are refetched while another creation is being prepared (QL-720). */
const CREATION_POLL_MS = 2000;
/** Stop polling after this long: the creation either finished or failed (its Idura phase is bounded at 30 s). */
const CREATION_POLL_GIVE_UP_MS = 35_000;

export function SignatureSection({
  flowInstanceId,
  pdfCount,
  refreshKey,
  clientId,
  clientEmail: initialEmail,
  clientMobilePhone: initialPhone,
  onSignatureChange,
  enableEidSigning = true,
  enableDigitalSigning = true,
}: SignatureSectionProps) {
  const { formatDate, formatDateTime } = useRegionalFormat();
  const t = useT(T);

  // eID signing also requires the tenant's Idura (Criipto) mode to be on (the
  // server enforces this on createCriiptoSignatureOrder). While the query loads,
  // assume enabled to avoid hiding it transiently. When only one method is
  // available we drop the "choose a method" chooser framing — it looks silly.
  const { features: tpFeatures } = useThirdPartyFeatures();
  const { iduraMode } = useIduraMode();
  const iduraEnabled = iduraMode ? iduraMode !== "disabled" : true;
  const sinchEnabled = tpFeatures?.sinchEnabled ?? true;
  const eidAvailable = enableEidSigning && iduraEnabled;
  const methodCount = (eidAvailable ? 1 : 0) + (enableDigitalSigning ? 1 : 0);

  const [isPending, startTransition] = useTransition();
  const [identityOpen, setIdentityOpen] = useState(false);
  const { identity } = useClientIdentity(clientId);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [qrModalUrl, setQrModalUrl] = useState<string | null>(null);
  const [email, setEmail] = useState(initialEmail || "");
  const [mobilePhone, setMobilePhone] = useState(initialPhone || "");
  const [inviteTab, setInviteTab] = useState<"email" | "sms">("email");
  const [signatureImageUrl, setSignatureImageUrl] = useState<string | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();
  const queryClient = useQueryClient();
  // The saved values reach this section as props from the parent's client
  // query, which the hook refreshes after a save.
  const saveClientContact = useSaveClientContact();

  // Signature orders are sourced from React Query so any invalidation of this key
  // refreshes the section live — including writes from the AI chat tools
  // (flow_signature_manage / flow_signature_delete), which can't reach imperative local state.
  const signaturesQueryKey = ["flowInstances", flowInstanceId, "signatures"] as const;
  // QL-720 — set when a create was refused because another is being prepared: poll until
  // that order shows up (or give up), so this tab does not sit on a stale "waiting" state.
  const [waitingForOrder, setWaitingForOrder] = useState(false);
  const { data: orders = [], isLoading } = useQuery({
    queryKey: signaturesQueryKey,
    queryFn: () => get<SignatureOrder[]>(`/internal/flow-instances/${flowInstanceId}/signatures`),
    refetchInterval: waitingForOrder ? CREATION_POLL_MS : false,
  });
  // Only an order that is still in progress ends the wait: a cancelled or failed one from
  // before is not the order the other tab is creating.
  const hasInProgressOrder = orders.some((o) => o.status === "pending" || o.status === "signing");
  // Through a ref, so a parent re-render (a new callback identity) does not restart the
  // give-up timer below.
  const onSignatureChangeRef = useRef(onSignatureChange);
  onSignatureChangeRef.current = onSignatureChange;
  useEffect(() => {
    if (!waitingForOrder) return;
    if (hasInProgressOrder) {
      setWaitingForOrder(false);
      onSignatureChangeRef.current?.();
      return;
    }
    const giveUp = setTimeout(() => setWaitingForOrder(false), CREATION_POLL_GIVE_UP_MS);
    return () => clearTimeout(giveUp);
  }, [waitingForOrder, hasInProgressOrder]);

  // Invalidate the shared signatures query so this section AND the parent documents
  // page re-derive hasActiveSignatureOrder / hasSignedOrder — otherwise the "PDF
  // Documents" tab keeps a stale lock state after a signing order is started or cancelled.
  const reloadOrders = async () => {
    await queryClient.invalidateQueries({ queryKey: signaturesQueryKey });
    onSignatureChange?.();
  };

  // Track if contact info has changed. An emptied field is not an edit: this
  // form never clears the stored contact, it only replaces it.
  const emailChanged = !!email.trim() && email !== (initialEmail || "");
  const phoneChanged = !!mobilePhone.trim() && mobilePhone !== (initialPhone || "");

  // Extract refresh key to a stable value for dependency array
  const refreshKeyValue = refreshKey ?? 0;

  // PDF generation/deletion bumps refreshKey — refetch orders so the lock state
  // re-derives. Skip the initial render (useQuery already does the first load).
  const prevRefreshKey = useRef(refreshKeyValue);
  useEffect(() => {
    if (prevRefreshKey.current === refreshKeyValue) return;
    prevRefreshKey.current = refreshKeyValue;
    queryClient.invalidateQueries({ queryKey: signaturesQueryKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKeyValue]);

  // Load signature image URL on-demand for simple signatures
  // This generates a fresh presigned URL instead of using a stored (possibly expired) one
  useEffect(() => {
    const signedOrder = orders.find((o) => o.status === "signed");
    if (signedOrder?.signatureMethod === "simple" && signedOrder.signatureImageS3Key) {
      get<{ success: boolean; data?: { url: string } }>(
        `/internal/flow-instances/signatures/${signedOrder.id}/image-url`,
      )
        .then((result) => {
          if (result.success && result.data) {
            setSignatureImageUrl(result.data.url);
          }
        })
        .catch(() => {});
    } else {
      setSignatureImageUrl(null);
    }
  }, [orders]);

  const handleCreateSignatureOrder = () => {
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        const result = await post<{ success: boolean; error?: string }>(
          `/internal/flow-instances/${flowInstanceId}/signatures/criipto`,
        );

        if (!result.success) {
          setError(result.error || t.failedToCreateOrder);
          return;
        }

        setSuccess(t.eidLinkCreated);

        await reloadOrders();
      } catch (err) {
        setError(apiErrorMessage(err, t.failedToCreateOrder));
        // Another creation is in progress for this flow: what is on screen is stale, so
        // refetch now and keep polling until the order appears (QL-720).
        if (apiErrorCode(err) === "signature_creation_in_progress") {
          setWaitingForOrder(true);
          await reloadOrders();
        }
      }
    });
  };

  const handleCreateSimpleSignatureOrder = () => {
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        const result = await post<{ success: boolean; error?: string }>(
          `/internal/flow-instances/${flowInstanceId}/signatures/simple`,
        );

        if (!result.success) {
          setError(result.error || t.failedToCreateOrder);
          return;
        }

        setSuccess(t.simpleLinkCreated);

        await reloadOrders();
      } catch (err) {
        setError(apiErrorMessage(err, t.failedToCreateOrder));
        // Another creation is in progress for this flow: what is on screen is stale, so
        // refetch now and keep polling until the order appears (QL-720).
        if (apiErrorCode(err) === "signature_creation_in_progress") {
          setWaitingForOrder(true);
          await reloadOrders();
        }
      }
    });
  };

  const handleCopyLink = (signToken: string, method: string) => {
    const path = method === "simple" ? "sign-simple" : "sign";
    const signUrl = `${window.location.origin}/p/${signToken}/${path}`;
    navigator.clipboard.writeText(signUrl);
    setSuccess(t.linkCopied);
    setTimeout(() => setSuccess(null), 3000);
  };

  // QL-605 — Save on each tab sends only that tab's field, so an unsaved edit
  // on the other tab stays unsaved. Save is only offered for a non-empty value.
  const handleSaveContact = (field: "email" | "mobilePhone") => {
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        const stored = await saveClientContact(
          clientId,
          field,
          field === "email" ? email : mobilePhone,
        );
        // QL-91 — the server stores the email trimmed and lowercased, so show
        // what it saved; comparing against the typed casing keeps Send disabled.
        if (field === "email") setEmail(stored);
        else setMobilePhone(stored);
        setSuccess(t.clientInfoUpdated);
      } catch (err: any) {
        setError(err?.message || t.failedToUpdateClient);
      }
    });
  };

  const handleSendInvite = (orderId: string) => {
    setError(null);
    setSuccess(null);

    if (inviteTab === "email") {
      if (!email) {
        setError(t.enterEmail);
        return;
      }

      startTransition(async () => {
        try {
          const result = await post<{
            success: boolean;
            error?: string;
            data?: { sentTo?: string };
          }>(`/internal/flow-instances/signatures/${orderId}/invite-email`);

          if (!result.success) {
            setError(result.error || t.failedToSendEmail);
            return;
          }

          setSuccess(t.inviteSentTo(result.data?.sentTo || email));
        } catch (err: any) {
          setError(apiErrorMessage(err, t.failedToSendEmail));
        }
      });
    } else {
      if (!mobilePhone) {
        setError(t.enterPhone);
        return;
      }

      startTransition(async () => {
        try {
          const result = await post<{
            success: boolean;
            error?: string;
            data?: { sentTo?: string };
          }>(`/internal/flow-instances/signatures/${orderId}/invite-sms`);

          if (!result.success) {
            setError(result.error || t.failedToSendSms);
            return;
          }

          setSuccess(t.inviteSentTo(result.data?.sentTo || mobilePhone));
        } catch (err: any) {
          // The SMS invite falls back to e-mail when SMS is off for the tenant.
          setError(apiErrorMessage(err, t.failedToSendSms));
        }
      });
    }
  };

  const handleDownloadSigned = async (
    signedPdfId: string,
    _templateName: string,
    orderId: string,
  ) => {
    startTransition(async () => {
      try {
        const result = await get<{ success: boolean; error?: string; data?: { url: string } }>(
          `/internal/flow-instances/signatures/${orderId}/pdf-url/${signedPdfId}`,
        );

        if (!result.success || !result.data) {
          setError(result.error || t.failedToGetDownload);
          return;
        }

        window.open(result.data.url, "_blank");
      } catch (err: any) {
        setError(err?.message || t.failedToDownload);
      }
    });
  };

  const handleCancelOrder = async (orderId: string, _orderStatus: string) => {
    const confirmed = await confirm({
      title: t.deleteSignatureRequest,
      description: t.deleteConfirmation,
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) {
      return;
    }

    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        const result = await del<{ success: boolean; error?: string }>(
          `/internal/flow-instances/signatures/${orderId}`,
        );

        if (!result.success) {
          setError(result.error || t.failedToDelete);
          return;
        }

        setSuccess(t.deletedSuccess);

        await reloadOrders();
      } catch (err) {
        setError(apiErrorMessage(err, t.failedToDelete));
        // A creation is being prepared for this flow, so what is on screen may be stale:
        // refetch the orders (QL-720).
        if (apiErrorCode(err) === "signature_creation_in_progress") await reloadOrders();
      }
    });
  };

  const handleRefreshStatus = (orderId: string) => {
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        const result = await post<{ success: boolean; error?: string; data?: { status?: string } }>(
          `/internal/flow-instances/signatures/${orderId}/refresh`,
        );

        if (!result.success) {
          setError(result.error || t.failedToRefresh);
          return;
        }

        if (result.data?.status === "signed") {
          setSuccess(t.documentSigned);
        } else {
          setSuccess(t.statusUpdated(result.data?.status || "unknown"));
        }

        await reloadOrders();
      } catch (err: any) {
        setError(err?.message || t.failedToRefresh);
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <Badge variant="success">{t.signed}</Badge>;
      case "signing":
        return <Badge variant="secondary">{t.inProgress}</Badge>;
      case "pending":
        return <Badge variant="secondary">{t.pending}</Badge>;
      case "expired":
        return <Badge variant="destructive">{t.expired}</Badge>;
      case "cancelled":
        return <Badge variant="destructive">{t.cancelled}</Badge>;
      case "failed":
        return <Badge variant="destructive">{t.failed}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            {t.digitalSignatures}
          </CardTitle>
          <CardDescription>{t.signatureDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (pdfCount === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            {t.digitalSignatures}
          </CardTitle>
          <CardDescription>{t.signatureDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t.noPdfsToSign}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeOrder = orders.find((o) => o.status === "pending" || o.status === "signing");
  const signedOrder = orders.find((o) => o.status === "signed");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="h-5 w-5" />
          {t.digitalSignatures}
        </CardTitle>
        <CardDescription>{t.signatureDescriptionWithCount(pdfCount)}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert variant="success">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Create signature order if none exists */}
        {!activeOrder && !signedOrder && (
          <div className="space-y-4">
            {/* Only frame it as a choice when there's actually more than one method. */}
            {methodCount > 1 && (
              <div>
                <p className="text-sm font-medium mb-2">{t.chooseSignatureMethod}</p>
                <p className="text-sm text-muted-foreground">{t.selectHowToSign}</p>
              </div>
            )}

            {methodCount === 0 && (
              <p className="text-sm text-muted-foreground">{t.noSignatureMethod}</p>
            )}

            <div className={methodCount > 1 ? "grid gap-3 sm:grid-cols-2" : "grid gap-3"}>
              {/* eID Signature */}
              {eidAvailable && (
                <Card
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={!isPending ? handleCreateSignatureOrder : undefined}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileSignature className="h-4 w-4" />
                      {t.eidSignature}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{t.eidDescription}</p>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-success" />
                        {t.legallyBinding}
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-success" />
                        {t.identityVerified}
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-success" />
                        {t.bestForNordics}
                      </div>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateSignatureOrder();
                      }}
                      loading={isPending}
                      className="w-full gap-2"
                      size="sm"
                    >
                      {t.createEidRequest}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Simple Signature */}
              {enableDigitalSigning && (
                <Card
                  className="cursor-pointer hover:border-primary transition-colors"
                  onClick={!isPending ? handleCreateSimpleSignatureOrder : undefined}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Pen className="h-4 w-4" />
                      {t.simpleSignature}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">{t.simpleDescription}</p>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-success" />
                        {t.quickEasy}
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-success" />
                        {t.recordsEvidence}
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-success" />
                        {t.bestForOthers}
                      </div>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateSimpleSignatureOrder();
                      }}
                      loading={isPending}
                      className="w-full gap-2"
                      size="sm"
                      variant="outline"
                    >
                      {t.createSimpleRequest}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Active signature order */}
        {activeOrder && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{t.signatureRequest}</h4>
                <p className="text-sm text-muted-foreground">{t.shareLink}</p>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(activeOrder.status)}
                {activeOrder.signatureMethod !== "simple" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRefreshStatus(activeOrder.id)}
                    disabled={isPending}
                    className="gap-2"
                  >
                    {t.checkStatus}
                  </Button>
                )}
                <Button
                  size="icon"
                  tooltip={t.delete}
                  variant="ghost"
                  onClick={() => handleCancelOrder(activeOrder.id, activeOrder.status)}
                  disabled={isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium">{t.signingLink}</span>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono truncate">
                  {window.location.origin}/p/{activeOrder.signToken}/
                  {activeOrder.signatureMethod === "simple" ? "sign-simple" : "sign"}
                </div>
                <Button
                  size="icon"
                  tooltip={t.showSigningQr}
                  variant="outline"
                  onClick={() => {
                    const url = `${window.location.origin}/p/${activeOrder.signToken}/${activeOrder.signatureMethod === "simple" ? "sign-simple" : "sign"}`;
                    setQrModalUrl(url);
                  }}
                >
                  <QrCode className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  tooltip={t.copy}
                  variant="outline"
                  onClick={() => handleCopyLink(activeOrder.signToken, activeOrder.signatureMethod)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {activeOrder.signatureMethod === "simple"
                  ? t.simpleSignatureLabel
                  : t.eidSignatureLabel}{" "}
                • {t.expires} {formatDate(parseDbTimestamp(activeOrder.signTokenExpiry))}
              </p>
            </div>

            {/* Send invitation */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={inviteTab === "email" ? "default" : "outline"}
                  onClick={() => setInviteTab("email")}
                >
                  {t.email}
                </Button>
                {/* SMS hidden when the tenant has Sinch off — server won't send it. */}
                {sinchEnabled && (
                  <Button
                    size="sm"
                    variant={inviteTab === "sms" ? "default" : "outline"}
                    onClick={() => setInviteTab("sms")}
                  >
                    {t.sms}
                  </Button>
                )}
              </div>

              {inviteTab === "email" || !sinchEnabled ? (
                <div className="space-y-2">
                  <Label htmlFor="signature-email">{t.emailAddress}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="signature-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="client@example.com"
                      className="flex-1"
                    />
                    {emailChanged && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSaveContact("email")}
                        disabled={isPending}
                      >
                        {t.save}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleSendInvite(activeOrder.id)}
                      disabled={!email.trim() || emailChanged}
                      loading={isPending}
                    >
                      {t.send}
                    </Button>
                  </div>
                  {!email.trim() && (
                    <p className="text-xs text-muted-foreground">{t.enterEmailToSend}</p>
                  )}
                  {emailChanged && <p className="text-xs text-warning">{t.saveEmailFirst}</p>}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="signature-phone">{t.mobilePhone}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="signature-phone"
                      type="tel"
                      value={mobilePhone}
                      onChange={(e) => setMobilePhone(e.target.value)}
                      placeholder="+47 XXX XX XXX"
                      className="flex-1"
                    />
                    {phoneChanged && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSaveContact("mobilePhone")}
                        disabled={isPending}
                      >
                        {t.save}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleSendInvite(activeOrder.id)}
                      disabled={!mobilePhone.trim() || phoneChanged}
                      loading={isPending}
                    >
                      {t.send}
                    </Button>
                  </div>
                  {!mobilePhone.trim() && (
                    <p className="text-xs text-muted-foreground">{t.enterPhoneToSend}</p>
                  )}
                  {phoneChanged && <p className="text-xs text-warning">{t.savePhoneFirst}</p>}
                </div>
              )}
            </div>

            {/* Documents included */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-2">{t.documentsToSign}</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {activeOrder.pdfLogs.map((pdf: any) => (
                  <li key={pdf.id} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-info" />
                    {pdf.pdfLog.template?.name || "Document"}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Signed documents */}
        {signedOrder && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{t.signedDocuments}</h4>
                <p className="text-sm text-muted-foreground">
                  {t.documentsSignedOn}{" "}
                  {signedOrder.signedAt && formatDate(parseDbTimestamp(signedOrder.signedAt))}
                </p>
              </div>
              {getStatusBadge(signedOrder.status)}
            </div>

            {signedOrder.signerName && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="text-sm space-y-1">
                  <p className="flex items-center gap-2">
                    <span>
                      <span className="font-medium">{t.signer}</span> {signedOrder.signerName}
                    </span>
                    {/* QL-e — eID signatures only. A `simple` signature is a
                        drawn squiggle and establishes no identity at all, so a
                        green badge beside that signer's name — sourced from an
                        unrelated document check — would credit the signature
                        with an assurance it never carried. */}
                    {signedOrder.signatureMethod === "criipto" && (
                      <VerifiedIdBadge
                        summary={identity?.summary}
                        onOpen={() => setIdentityOpen(true)}
                      />
                    )}
                  </p>
                  {signedOrder.signerEidProvider && (
                    <p>
                      <span className="font-medium">{t.method}</span>{" "}
                      {signedOrder.signerEidProvider}
                    </p>
                  )}
                  {signedOrder.signatureMethod === "simple" && signedOrder.signerAuthMethod && (
                    <p>
                      <span className="font-medium">{t.verifiedVia}</span>{" "}
                      {signedOrder.signerAuthMethod === "email" ? t.email : t.sms}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Simple signature image */}
            {signedOrder.signatureMethod === "simple" && signatureImageUrl && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">{t.signature}</p>
                <div className="bg-white border rounded-lg p-4 inline-block">
                  <img src={signatureImageUrl} alt={t.signature} className="max-w-md h-auto" />
                </div>
              </div>
            )}

            <Table variant="data">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.document}</TableHead>
                  <TableHead>{t.signedDate}</TableHead>
                  <TableActionsHead>{t.actions}</TableActionsHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {signedOrder.pdfLogs.map((pdf: any) => (
                  <TableRow key={pdf.id}>
                    <TableCell className="font-medium">
                      {pdf.pdfLog.template?.name || t.document}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {pdf.signedAt ? formatDateTime(parseDbTimestamp(pdf.signedAt)) : "-"}
                    </TableCell>
                    <TableActionsCell>
                      {pdf.signedS3Key && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleDownloadSigned(
                              pdf.id,
                              pdf.pdfLog.template?.name || t.document,
                              signedOrder.id,
                            )
                          }
                          disabled={isPending}
                          tooltip={t.download}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </TableActionsCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* All signature orders history */}
        {orders.length > 0 && (
          <div className="pt-6 border-t">
            <h4 className="text-sm font-medium mb-3">{t.signatureHistory}</h4>
            <div className="space-y-2">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="text-sm">
                    <p className="font-medium">
                      {order.pdfLogs.length} {t.documents(order.pdfLogs.length)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.created} {formatDateTime(parseDbTimestamp(order.createdAt))}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(order.status)}
                    {order.status !== "signed" && (
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => handleCancelOrder(order.id, order.status)}
                        disabled={isPending}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      <ConfirmationDialog />
      <QRCodeModal
        open={!!qrModalUrl}
        onClose={() => setQrModalUrl(null)}
        url={qrModalUrl || ""}
        title={t.signingLinkTitle}
      />

      <VerifiedIdDialog
        open={identityOpen}
        onClose={() => setIdentityOpen(false)}
        verifications={identity?.verifications ?? []}
      />
    </Card>
  );
}
